const ENDPOINT = 'https://api.web3forms.com/submit';
const TIMEOUT_MS = 15_000;

export interface ContactSubmission {
  accessKey: string;
  /** Where the reply goes — Web3Forms uses this as the reply-to. */
  email: string;
  subject: string;
  message: string;
  /** The honeypot's value. Anything non-empty means a bot filled the hidden field. */
  botcheck: string;
}

export type SendResult =
  | { ok: true }
  /** `network`: never reached them (offline, timeout) — safe to retry. `rejected`: they answered no. */
  | { ok: false; reason: 'network' | 'rejected' };

/**
 * Sends a contact message through Web3Forms.
 *
 * Never throws: the caller renders one of two failure states, and both keep
 * what the person wrote. This is a PWA, so "offline" is an ordinary way for
 * this to fail, not an exceptional one.
 */
export async function sendContact(
  submission: ContactSubmission,
  fetchImpl: typeof fetch = fetch,
): Promise<SendResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetchImpl(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: submission.accessKey,
        from_name: 'Contact form',
        email: submission.email,
        subject: submission.subject,
        message: submission.message,
        botcheck: submission.botcheck,
      }),
      signal: controller.signal,
    });

    const data = (await res.json().catch(() => null)) as { success?: boolean } | null;
    return res.ok && data?.success === true ? { ok: true } : { ok: false, reason: 'rejected' };
  } catch {
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
  }
}
